import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import SignInForm from './SignInForm';
import SignUpForm from './SignUpForm';

const AuthForm = () => {
  const [mode, setMode] = useState('signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSignIn = async ({ email, password }) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: signInError } = await signIn(email, password);
      if (signInError) {
        if (signInError?.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (signInError?.message?.includes('Email not confirmed')) {
          setError('Please verify your email before signing in.');
        } else {
          setError(signInError?.message || 'Sign in failed. Please try again.');
        }
      } else if (data?.user) {
        navigate('/all-projects');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async ({ fullName, email, password }) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `https://mibuy2878.builtwithrocket.new/auth/confirm`,
        },
      });
      if (signUpError) {
        if (signUpError?.message?.includes('already registered')) {
          setError('An account with this email already exists. Please sign in.');
        } else if (signUpError?.message?.includes('Password should be')) {
          setError('Password must be at least 6 characters long.');
        } else {
          setError(signUpError?.message || 'Sign up failed. Please try again.');
        }
      } else if (data?.user) {
        // Welcome email is sent after email verification completes in /auth/confirm
        setSuccess('Account created! Please check your email to confirm your account before signing in.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (email) => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/login-sign-up`,
      });
      if (resetError) {
        setError(resetError?.message || 'Failed to send reset email.');
      } else {
        setSuccess('Password reset email sent! Check your inbox.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
  };

  return (
    <div className="w-full max-w-md">
      {/* Card */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        {/* Tab Toggle */}
        <div className="flex">
          <button
            onClick={() => switchMode('signin')}
            className={`flex-1 py-4 text-sm font-semibold transition-all duration-200 ${
              mode === 'signin' ?'text-white' :'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            style={mode === 'signin' ? { background: 'linear-gradient(to right, #F58A54, #F06F6B)' } : {}}
          >
            Sign In
          </button>
          <button
            onClick={() => switchMode('signup')}
            className={`flex-1 py-4 text-sm font-semibold transition-all duration-200 ${
              mode === 'signup' ?'text-white' :'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            style={mode === 'signup' ? { background: 'linear-gradient(to right, #F06F6B, #E0528A)' } : {}}
          >
            Sign Up
          </button>
        </div>

        <div className="p-8">
          {/* Error Alert */}
          {error && (
            <div className="mb-5 p-3.5 rounded-xl border flex items-start gap-3" style={{ backgroundColor: '#FFF5F3', borderColor: '#F58A54' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#F06F6B' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm" style={{ color: '#E0528A' }}>{error}</p>
            </div>
          )}

          {/* Success Alert */}
          {success && (
            <div className="mb-5 p-3.5 rounded-xl border flex items-start gap-3" style={{ backgroundColor: '#F0FFF4', borderColor: '#48BB78' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}

          {mode === 'signin' ? (
            <SignInForm
              onSubmit={handleSignIn}
              onForgotPassword={handleForgotPassword}
              loading={loading}
            />
          ) : (
            <SignUpForm
              onSubmit={handleSignUp}
              loading={loading}
            />
          )}
        </div>
      </div>

      {/* Trust badges */}
      <div className="mt-6 flex items-center justify-center gap-6">
        <div className="flex items-center gap-1.5 text-white/80">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span className="text-xs">SSL Secured</span>
        </div>
        <div className="flex items-center gap-1.5 text-white/80">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span className="text-xs">Privacy Protected</span>
        </div>
        <div className="flex items-center gap-1.5 text-white/80">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-xs">Instant Access</span>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
